import * as React from "react"
import { Outlet, useLocation, useNavigate } from "react-router"

import { AppShell, NAV, type NavRole } from "@/components/shell"
import { RequireAuth, useCurrentUser, useLogoutMutation } from "@/features/auth"
import { useQueueTotalsQuery } from "@/features/queue/queue-api"
import { Role, toRole } from "@/lib/roles"
import { useTheme } from "@/lib/use-theme"

// Wraps every route with AppShell and owns app-level concerns (theme, nav map,
// current href, sign-out) so route components don't have to. Guarded by
// RequireAuth so an unauth reviewer is bounced to /login before this shell
// tries to render any data.

// UI display labels. The SME role is shown as "Specialist" (friendlier vocabulary), so
// this map is *display* concern only — the wire vocabulary lives in `lib/roles`.
const ROLE_LABEL: Record<NavRole, string> = {
  [Role.ADMIN]: "Admin",
  [Role.REVIEWER]: "Reviewer",
  [Role.SME]: "Specialist",
  [Role.VIEWER]: "Viewer",
  [Role.CLIENT]: "Client",
}

export function ShellRoute() {
  return (
    <RequireAuth>
      <AuthenticatedShell />
    </RequireAuth>
  )
}

function AuthenticatedShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const currentUser = useCurrentUser()
  const { logout } = useLogoutMutation()
  // The badge counts work that is still open, so a cleared queue shows nothing
  // at all — a zero would read as a number worth checking.
  const { data: queueTotals } = useQueueTotalsQuery()

  const currentHref = location.pathname

  const activeId =
    NAV.flatMap((g) => g.items)
      .filter((item) => currentHref === item.href || currentHref.startsWith(`${item.href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0]?.id ?? ""

  const routeTitle = titleFor(currentHref, activeId)

  // Unknown role → viewer (safest non-client fallback for an authenticated staff user).
  const role: NavRole = (currentUser && toRole(currentUser.role)) ?? Role.VIEWER
  const user = {
    name: currentUser?.name ?? currentUser?.email ?? "",
    email: currentUser?.email ?? "",
    roleLabel: ROLE_LABEL[role],
  }

  const { counts } = queueTotals ?? {}
  const open = counts ? counts.queued + counts.in_review + counts.needs_sme : undefined
  const nav = React.useMemo(
    () =>
      NAV.map((group) => ({
        ...group,
        items: group.items.map((item) =>
          item.id === "queue" ? { ...item, badge: open || undefined } : item,
        ),
      })),
    [open],
  )

  return (
    <AppShell
      nav={nav}
      role={role}
      activeId={activeId}
      routeTitle={routeTitle}
      currentHref={currentHref}
      user={user}
      theme={theme}
      onThemeChange={setTheme}
      onNavigate={(href) => {
        void navigate(href)
      }}
      onSignOut={() => {
        logout()
        void navigate("/login", { replace: true })
      }}
    >
      <Outlet />
    </AppShell>
  )
}

function titleFor(href: string, activeId: string): string {
  const item = NAV.flatMap((g) => g.items).find((i) => i.id === activeId)
  if (item) return item.label
  if (href === "/" || href === "") return "Welcome"
  return "Pyxis"
}
